import { useEffect, useState } from 'react';
import { Gift, Link as LinkIcon, Copy, Users, DollarSign } from 'react-feather';
import { authAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrencyWithConversion } from '../utils/currency';
import AdsDisplay from '../components/AdsDisplay';
import './ReferralProgram.css';

export default function ReferralProgram() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [program, setProgram] = useState(null);

  useEffect(() => {
    authAPI.referralProgram()
      .then((response) => setProgram(response.data))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load referral program'))
      .finally(() => setLoading(false));
  }, []);

  const handleCopy = async () => {
    if (!program?.referral?.link) return;
    try {
      await navigator.clipboard.writeText(program.referral.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  const referral = program?.referral || {};
  const settings = program?.program || {};
  const recentInvites = program?.recent_invites || [];
  const recentRewards = program?.recent_rewards || [];

  return (
    <div className="referral-page">
      <h1 className="page-title">Referral Program</h1>
      <p className="page-subtitle">Invite users, earn deposit commissions for life, and let your invitees start with discounted orders.</p>

      {!settings.enabled && (
        <div className="alert alert-error" style={{ marginBottom: 20 }}>
          The referral program is currently disabled by the admin.
        </div>
      )}

      <div className="referral-grid">
        <div className="card referral-hero">
          <div className="referral-hero-icon"><Gift size={22} /></div>
          <h3>Your invite link</h3>
          <p>
            Earn {settings.commission_percent}% whenever your invitees deposit. New users who join with your link get {settings.invitee_discount_percent}% off their first {settings.invitee_discount_order_limit} orders.
          </p>
          <div className="referral-link-box">
            <LinkIcon size={16} />
            <input value={referral.link || ''} readOnly />
            <button type="button" className="btn btn-primary" onClick={handleCopy}>
              <Copy size={16} /> {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <div className="referral-code-line">Referral code: <strong>{referral.code || 'Not available yet'}</strong></div>
        </div>

        <div className="card referral-stats">
          <div className="referral-stat">
            <span className="referral-stat-icon"><Users size={16} /></span>
            <div>
              <div className="referral-stat-label">Total invites</div>
              <div className="referral-stat-value">{referral.total_invites || 0}</div>
            </div>
          </div>
          <div className="referral-stat">
            <span className="referral-stat-icon"><DollarSign size={16} /></span>
            <div>
              <div className="referral-stat-label">Lifetime earned</div>
              <div className="referral-stat-value">
                {formatCurrencyWithConversion(referral.total_earned || 0, user?.currency || 'USD', 4)}
              </div>
            </div>
          </div>
          <div className="referral-stat">
            <span className="referral-stat-icon"><Gift size={16} /></span>
            <div>
              <div className="referral-stat-label">Your invitee discount progress</div>
              <div className="referral-stat-value">
                {Math.max(0, referral.discount_orders_remaining || 0)} orders left
              </div>
              <div className="referral-stat-note">
                Used {referral.discount_orders_used || 0} of {settings.invitee_discount_order_limit || 0} discounted orders if you joined through a referral.
              </div>
            </div>
          </div>
        </div>
      </div>

      <AdsDisplay adSlot="native-banner" className="native-banner" />

      <div className="referral-panels">
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Recent invites</h3>
          {recentInvites.length ? recentInvites.map((invite) => (
            <div key={invite.id} className="referral-row">
              <div className="referral-row-main">
                <div className="referral-row-title">{invite.username}</div>
                <div className="referral-row-sub">{invite.email}</div>
              </div>
              <div className="referral-row-meta">
                <div className="referral-row-title">{formatCurrencyWithConversion(invite.total_commission || 0, user?.currency || 'USD', 4)}</div>
                <div className="referral-row-sub">{new Date(invite.created_at).toLocaleDateString()}</div>
              </div>
            </div>
          )) : <div className="referral-empty">No invited users yet.</div>}
        </div>

        <AdsDisplay adSlot="banner-320x50" className="referral-middle-banner" />

        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Recent commissions</h3>
          {recentRewards.length ? recentRewards.map((reward) => (
            <div key={reward.id} className="referral-row">
              <div className="referral-row-main">
                <div className="referral-row-title">{reward.username}</div>
                <div className="referral-row-sub">
                  Deposit {formatCurrencyWithConversion(reward.deposit_amount || 0, user?.currency || 'USD', 4)}
                </div>
              </div>
              <div className="referral-row-meta">
                <div className="referral-row-title">{formatCurrencyWithConversion(reward.commission_amount || 0, user?.currency || 'USD', 4)}</div>
                <div className="referral-row-sub">{reward.commission_percent}%</div>
              </div>
            </div>
          )) : <div className="referral-empty">No commissions yet.</div>}
        </div>
      </div>
    </div>
  );
}
